import { Avatar, Chip, Tooltip } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { TrashIcon } from '../components/Icons';
import { ETheme } from '../enums/user/ETheme';
import {
  getEmployeeContractColor,
  getWorkModeColor,
} from '../functions/getChipColors';
import { getUserInitials } from '../functions/getUserInitials';
import { IUser } from '../interfaces/user/IUser';
import { darkTheme, lightTheme } from '../mui/theme';

export const useDepartmentUserColumns = (
  setSelectedItem: (user: IUser) => void,
  setShowDeleteModal: (show: boolean) => void,
  isNormalUser: boolean,
): GridColDef[] => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.auth);
  return [
    { field: 'id', headerName: t('general.id'), width: 50 },
    {
      field: 'displayName',
      headerName: t('departments.employeeName'),
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            style={{ marginRight: '8px' }}
            src={params.row?.profilePhoto?.thumbnail}
          >
            {getUserInitials(params.row)}
          </Avatar>
          {params.row?.displayName}
        </div>
      ),
    },
    {
      field: 'workMode',
      headerName: t('general.workMode'),
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Chip
          label={t(`workMode.${params?.row?.workMode}`)}
          variant={'outlined'}
          color={getWorkModeColor(params?.row?.workMode)}
        />
      ),
    },
    {
      field: 'employeeContract',
      headerName: t('general.employeeContract'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Chip
          label={t(`employeeContract.${params?.row?.employeeContract}`)}
          variant={'outlined'}
          color={getEmployeeContractColor(params?.row?.employeeContract)}
        />
      ),
    },
    {
      field: 'actions',
      headerName: t('general.action'),
      width: isNormalUser ? 0 : 150,
      sortable: false,
      headerClassName: isNormalUser ? 'hideColumn' : '',
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        return (
          <>
            {!isNormalUser && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                {/* Delete Icon */}
                <Tooltip title={t('general.delete')}>
                  <span
                    className={'d-flex'}
                    onClick={() => {
                      setSelectedItem(params?.row);
                      setShowDeleteModal(true);
                    }}
                  >
                    <TrashIcon
                      fill={
                        userData?.theme === ETheme.LIGHT
                          ? lightTheme.palette.text.primary
                          : darkTheme.palette.text.primary
                      }
                    />
                  </span>
                </Tooltip>
              </div>
            )}
          </>
        );
      },
    },
  ];
};
